import Encoding from 'encoding-japanese';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDropzone } from 'react-dropzone';
import { readString } from 'react-papaparse';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import Button from '../../../atoms/Buttons';
import FA from '../../../atoms/FontAwesome';

export type CSVRowData = {
  customer_id: string;
  money_amount: number;
  point_amount: number;
  description?: string;
};

type RewardCSVUploaderProps = {
  onCSVDataChange: (data: CSVRowData[]) => void;
};

const SectionWrapper = styled.div`
  margin: 0.5rem;
`;

const ExternalLink = styled.a`
  color: #46ba73;
  text-decoration: underline !important;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }

  &:visited {
    color: #46ba73;
  }
`;

const Dropzone = styled.div<{ fileSelected?: boolean }>`
  border: 2px dashed #d6d3d0;
  background-color: #f8f7f6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ fileSelected = false }) => (fileSelected ? 'flex-start' : 'center')};
  gap: 16px;
  padding: 8px;
  min-height: 200px;
  cursor: default;
  position: relative;
`;

const DropzoneText = styled.p`
  font-size: 20px;
  color: #706d65;
  text-align: center;
  margin: 0;
`;

const FileSelectButton = styled(Button)`
  color: black;
  font-size: 20px;
  display: flex;
  gap: 12px;
  padding: 12px 20px;
  border-radius: 8px;
`;

const FileClearButton = styled(FA)`
  padding: 4px;
  cursor: pointer;
`;

function readContentString(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (!event.target || !event.target.result) {
        reject();
        return;
      }

      /**
       * CSV Encoding Fix for Japanese Characters
       *
       * Problem:
       * Japanese characters in CSV files were being corrupted during upload (e.g., "ですく" became "ã§ããï¼").
       *
       * Root Cause:
       * - FileReader.readAsText() reads the file as a string using the browser's default encoding
       * - Encoding.detect() from encoding-japanese library expects a byte array (Uint8Array), not a string
       * - When passed a string, Encoding.detect() cannot properly detect the file's actual encoding
       * - This caused incorrect encoding conversion, corrupting multi-byte characters like Japanese text
       *
       * Solution:
       * 1. Read the file as ArrayBuffer (raw bytes) instead of text
       * 2. Convert ArrayBuffer to Uint8Array for byte-level processing
       * 3. Use Encoding.detect() on the byte array to properly detect encoding (UTF-8, Shift-JIS, etc.)
       * 4. Convert bytes to UNICODE array using the detected encoding
       * 5. Convert UNICODE array to UTF-8 string using Encoding.codeToString()
       *
       * This ensures proper encoding detection and conversion, preserving Japanese and other multi-byte characters.
       */

      const buffer = event.target.result as ArrayBuffer;
      const codes = new Uint8Array(buffer);

      const detectedEncoding = Encoding.detect(codes);

      const unicodeArray = Encoding.convert(codes, {
        to: 'UNICODE',
        from: detectedEncoding || 'AUTO',
      });

      const utf8String = Encoding.codeToString(unicodeArray).replace(/^\uFEFF/, '');

      resolve(utf8String);
    };
    reader.onerror = () => reject();

    reader.readAsArrayBuffer(file);
  });
}

async function getCSVData(csvString: string, header = true) {
  return new Promise<unknown[]>((resolve) => {
    readString(csvString, {
      worker: true,
      header,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        resolve(results.data);
      },
    });
  });
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const validateCSVRow = (row: CSVRowData, idx: number) => {
  const errors: { line: number; message: string }[] = [];
  const lineNumber = idx + 2;

  if (!row.customer_id) {
    errors.push({ line: lineNumber, message: 'customer_idを入力してください' });
  }

  const hasMoneyAmount = row.money_amount !== undefined && String(row.money_amount) !== '';
  const hasPointAmount = row.point_amount !== undefined && String(row.point_amount) !== '';

  if (!hasMoneyAmount && !hasPointAmount) {
    errors.push({ line: lineNumber, message: 'money_amountを入力してください' });
    errors.push({ line: lineNumber, message: 'point_amountを入力してください' });
  }

  if (row.customer_id && !UUID_REGEX.test(row.customer_id)) {
    errors.push({ line: lineNumber, message: 'customer_idが不正です' });
  }

  const moneyAmount = Number(row.money_amount);
  const pointAmount = Number(row.point_amount);

  if (hasMoneyAmount) {
    if (isNaN(moneyAmount) || moneyAmount < 0 || !Number.isInteger(moneyAmount)) {
      errors.push({ line: lineNumber, message: 'money_amountには数字を入力してください' });
    }
  }

  if (hasPointAmount) {
    if (isNaN(pointAmount) || pointAmount < 0 || !Number.isInteger(pointAmount)) {
      errors.push({ line: lineNumber, message: 'point_amountには数字を入力してください' });
    }
  }

  if (moneyAmount === 0 && pointAmount === 0) {
    errors.push({ line: lineNumber, message: '付与金額を入力してください' });
  }

  if (row.description && row.description.trim().length > 200) {
    errors.push({ line: lineNumber, message: 'descriptionは200文字以内で入力してください' });
  }

  return errors;
};

const validateCSVData = async (data: CSVRowData[]) => {
  const errors: { line: number; message: string }[] = [];
  data.forEach((row, idx) => {
    const rowErrors = validateCSVRow(row, idx);
    errors.push(...rowErrors);
  });
  return errors;
};

const RewardCSVUploader = ({ onCSVDataChange }: RewardCSVUploaderProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<
    'zero-byte' | 'invalid-format' | 'none' | 'csv-errors'
  >('none');
  const [csvErrors, setCsvErrors] = useState<{ line: number; message: string }[]>([]);

  const { getRootProps, getInputProps, open } = useDropzone({
    maxFiles: 1,
    accept: '.csv',
    noClick: true,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setFileError('invalid-format');
        setSelectedFile(null);
        onCSVDataChange([]);
        return;
      }
      if (acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles[0]);
        setFileError('none');
      }
    },
  });

  useEffect(() => {
    if (!selectedFile) {
      setFileError('none');
      setCsvErrors([]);
      onCSVDataChange([]);
      return;
    }

    if (selectedFile.size === 0) {
      setFileError('zero-byte');
      onCSVDataChange([]);
      return;
    }

    readContentString(selectedFile).then(async (csvString) => {
      if (csvString.trim().length === 0) {
        setFileError('zero-byte');
        onCSVDataChange([]);
        return;
      }

      // csvString is already properly converted to UTF-8 in readContentString
      const data = await getCSVData(csvString);

      const csvErrors = await validateCSVData(data as CSVRowData[]);
      if (csvErrors.length > 0) {
        setCsvErrors(csvErrors);
        setFileError('csv-errors');
        onCSVDataChange([]);
        return;
      }

      const transformedData: CSVRowData[] = data.map((row: CSVRowData) => ({
        customer_id: row.customer_id,
        money_amount: Number(row.money_amount) || 0,
        point_amount: Number(row.point_amount) || 0,
        description: row.description || undefined,
      }));

      onCSVDataChange(transformedData);
      setFileError('none');
      setCsvErrors([]);
    });
  }, [selectedFile, onCSVDataChange]);

  const uniqueErrorLines = Array.from(new Set(csvErrors.map((x) => x.line))).length;

  return (
    <SectionWrapper>
      <label>
        CSVファイル上でユーザごとのマネー/ポイント金額を指定します。作成方法は
        <ExternalLink
          href="https://www.notion.so/pocket-change/CSV-18c068200e108002b023cf712542ef59"
          target="_blank"
          rel="noopener noreferrer"
        >
          こちら
        </ExternalLink>
      </label>
      <Dropzone {...getRootProps()} fileSelected={!!selectedFile}>
        <input {...getInputProps()} />
        {selectedFile ? (
          <>
            <span>
              <b>選択中のファイル</b>
            </span>
            <span>
              <b>
                {selectedFile.name}{' '}
                <FileClearButton icon="times-circle" onClick={() => setSelectedFile(null)} />
              </b>
            </span>
          </>
        ) : (
          <>
            <DropzoneText>ここにドラッグ&ドロップ</DropzoneText>
            <DropzoneText>または</DropzoneText>
            <FileSelectButton onClick={open} variant="muted-outline">
              <FA icon="plus-circle" /> ファイルを選択
            </FileSelectButton>
          </>
        )}
      </Dropzone>
      <Alert
        isOpen={fileError !== 'none'}
        color="danger"
        style={{ marginTop: '0.5rem', color: '#721C24' }}
      >
        {fileError === 'zero-byte' && 'ファイル容量が0byteになっています'}
        {fileError === 'invalid-format' && 'CSVファイルが無効です'}
        {fileError === 'csv-errors' && (
          <div>
            <p style={{ marginBottom: '0.25rem' }}>{uniqueErrorLines}件の登録に失敗しました</p>
            <p style={{ marginBottom: '0.125rem' }}>
              <CSVLink
                data={csvErrors.map(({ line, message }) => [`${line}行目：${message}`])}
                filename={`Error_${moment().format('YYYYMMDDHHmmss')}.csv`}
                headers={[]}
                style={{ color: '#F00', textDecoration: 'underline' }}
              >
                エラーファイル
              </CSVLink>
              からエラー箇所を確認してください
            </p>
          </div>
        )}
      </Alert>
    </SectionWrapper>
  );
};

export default RewardCSVUploader;
